'use client'

import { Cell, Icon, Image, Spacer, Text } from '@vinted/web-ui'

import { Clock16, LocationPin16 } from '@vinted/monochrome-icons'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  DropOffPointModel,
  DropOffPointCarrierModel,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

enum DropOffPointStatus {
  Open = 'open',
  Closed = 'closed',
  Default = 'default',
}

type Props = {
  dropOffPoint?: DropOffPointModel
  dropOffPointCarrier?: DropOffPointCarrierModel | null
  navigating?: boolean
  chevron?: boolean
  onClick?: () => void
}

const DropOffPointInfoCard = ({
  dropOffPoint,
  dropOffPointCarrier,
  navigating,
  chevron,
  onClick,
}: Props) => {
  const renderLockerTypeSection = () => (
    <Cell
      styling="tight"
      theme="transparent"
      prefix={<Image src={dropOffPoint?.descriptionIconUrl} fallbackSrc="" size="small" alt="" />}
      body={
        <div className="u-flexbox u-align-items-center u-gap-medium">
          <Text
            as="h3"
            text={dropOffPoint?.name}
            type="subtitle"
            testId="drop-off-point-locker-type"
          />
        </div>
      }
    />
  )

  const renderDropOffPointSection = (
    icon: React.ComponentProps<typeof Icon>['name'],
    text: string,
    theme?: React.ComponentProps<typeof Text>['theme'],
  ) => (
    <div className="u-flexbox u-gap-medium">
      <Icon name={icon} color="greyscale-level-2" />
      <Text as="h3" text={text} type="subtitle" theme={theme} />
    </div>
  )

  const getFieldTheme = (status: string) => {
    switch (status) {
      case DropOffPointStatus.Open:
        return 'success'
      case DropOffPointStatus.Closed:
        return 'warning'
      default:
        return undefined
    }
  }

  const renderDropOffPointStatus = () => {
    if (!dropOffPoint) return null

    const textTheme = getFieldTheme(dropOffPoint.openingStatus.status)

    return (
      <>
        <Spacer size="small" />
        {renderDropOffPointSection(Clock16, dropOffPoint.openingStatus.text, textTheme)}
      </>
    )
  }

  const renderShippingPointInfo = () => {
    if (!dropOffPoint) return null

    const { dropOffPointAddress } = dropOffPoint

    return (
      <SeparatedList separator={<Spacer size="small" />}>
        {renderLockerTypeSection()}
        {renderDropOffPointSection(LocationPin16, dropOffPointAddress)}
      </SeparatedList>
    )
  }

  const renderCarrierInfo = () => (
    <Cell
      styling="tight"
      theme="transparent"
      prefix={<Image src={dropOffPointCarrier?.iconUrl} fallbackSrc="" size="regular" alt="" />}
      body={
        <div className="u-flexbox u-align-items-center u-gap-medium">
          <Text
            as="h2"
            text={dropOffPointCarrier?.name}
            type="title"
            testId="drop-off-point-carrier-title"
          />
        </div>
      }
    />
  )

  return (
    <Cell
      title={renderCarrierInfo()}
      body={
        <>
          <Spacer size="medium" />
          {renderShippingPointInfo()}
          {renderDropOffPointStatus()}
        </>
      }
      type={navigating ? 'navigating' : undefined}
      onClick={onClick}
      testId="drop-off-point-info-card"
      chevron={chevron}
    />
  )
}

export default DropOffPointInfoCard
